import { create } from 'zustand';

/**
 * Signing back in without losing the page.
 *
 * A session can lapse under a half-finished form — the admin cookie lasts twelve hours and the
 * console is worked at a counter all day — and the old behaviour was a "Could not save" that
 * left the reader to discover they had been signed out, sign in again on another screen, and
 * type the form a second time. Instead the request that found the session gone waits here: a
 * dialog opens over the form, which stays mounted with its draft, and once the reader has signed
 * in the request is sent again as if nothing had happened. Dismissing the dialog fails the
 * request the way it would have failed anyway.
 *
 * One prompt serves every request that is waiting on it, so a screen that fired three calls when
 * the cookie lapsed asks once, not three times.
 */
export type ReauthKind = 'user' | 'admin';

/**
 * The slug of the portal the console is open at, `/x/{slug}`. The admin sign-in sends it as the
 * second secret, so the console reads it from its own address rather than holding a copy.
 */
export function portalSlug(pathname = location.pathname): string {
  return pathname.startsWith('/x/') ? decodeURIComponent(pathname.split('/')[2] ?? '') : '';
}

interface ReauthState {
  /** Which sign-in is being asked for, or null when nothing is waiting. */
  kind: ReauthKind | null;
  waiting: ((ok: boolean) => void)[];
  settle: (ok: boolean) => void;
}

export const useReauth = create<ReauthState>((set, get) => ({
  kind: null,
  waiting: [],
  settle: (ok) => {
    const { waiting } = get();
    set({ kind: null, waiting: [] });
    for (const resolve of waiting) resolve(ok);
  },
}));

/** Resolves true once the reader has signed back in, false if they dismissed the prompt. */
export function requestReauth(kind: ReauthKind): Promise<boolean> {
  return new Promise((resolve) => {
    const { waiting, kind: open } = useReauth.getState();
    // An admin prompt and a user prompt cannot both be open; the first one asked wins, and a
    // request for the other kind fails as it would have without this.
    if (open && open !== kind) return resolve(false);
    useReauth.setState({ kind, waiting: [...waiting, resolve] });
  });
}

/**
 * Whether a refusal means "your session is gone" rather than "that password was wrong".
 *
 * A user route says so plainly: 401 `unauthorized`. The sign-in routes answer 401 too, but with
 * `invalid_credentials`, and set-password runs on a scoped session the prompt cannot restore.
 *
 * An admin route cannot say so at all, because the console answers 404 rather than 401 so that
 * its existence never leaks. A 404 from `/admin/*` is therefore only a *suspect*; the caller asks
 * `/admin/session`, which is a 404 exactly when there is no session, before treating it as one.
 */
export function expiryKind(path: string, status: number, code: string): ReauthKind | 'admin-suspect' | null {
  // A punter's routes are signed into on their own page (`/signin`), never over this dialog,
  // which signs an advertiser or agency in.
  if (path.startsWith('/punter/')) return null;
  if (status === 401 && code === 'unauthorized' && !path.startsWith('/auth/set-password')) {
    return 'user';
  }
  if (
    status === 404 &&
    path.startsWith('/admin/') &&
    !path.startsWith('/admin/login') &&
    !path.startsWith('/admin/session')
  ) {
    return 'admin-suspect';
  }
  return null;
}
